import type { ReactNode } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import Badge from "@/app/components/Badge";
import ActionMenu, { type ActionMenuItem } from "@/app/components/ActionMenu";
import { formatDate } from "@/lib/data";
import { enquiryName, enquiryPhone, latestQuotation, statusLabel, type EnquiryData } from "../enquiry";

export type { EnquiryData };
export type EnqSortCol = "name" | "type" | "date" | "guests" | "status" | "received";

/** The one main button of a row, e.g. "Create quotation" */
export interface PrimaryAction {
  label: string;
  onClick: () => void;
}

function SortTh({ col, label, align = "left", sortCol, sortDir, onSort }: {
  col: EnqSortCol; label: string; align?: "left" | "center" | "right";
  sortCol: EnqSortCol | null; sortDir: "asc" | "desc";
  onSort: (c: EnqSortCol) => void;
}) {
  const Icon = sortCol !== col ? ChevronsUpDown : sortDir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th
      onClick={() => onSort(col)}
      aria-sort={sortCol === col ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
      className={`text-${align} px-4 py-3 text-[11px] font-bold text-stone-500 uppercase tracking-wide cursor-pointer select-none hover:bg-stone-100 whitespace-nowrap`}
    >
      {label}<Icon className={`inline w-3 h-3 ml-0.5 ${sortCol === col ? "text-amber-600" : "opacity-30"}`} />
    </th>
  );
}

interface EnquiryTableProps {
  enquiries: EnquiryData[];
  onOpen: (id: number) => void;
  primaryAction: (e: EnquiryData) => PrimaryAction | null;
  menuItems: (e: EnquiryData) => ActionMenuItem[];
  emptyState: ReactNode;
  sortCol?: EnqSortCol | null;
  sortDir?: "asc" | "desc";
  onSort?: (col: EnqSortCol) => void;
}

export default function EnquiryTable({
  enquiries,
  onOpen,
  primaryAction,
  menuItems,
  emptyState,
  sortCol = null,
  sortDir = "asc",
  onSort = () => {},
}: EnquiryTableProps) {
  return (
    <div className="hidden md:block bg-white rounded-xl border border-stone-200 overflow-hidden mb-4">
      {enquiries.length === 0 ? emptyState : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <SortTh col="name" label="Client" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="type" label="Type" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="date" label="Event date" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="guests" label="Guests" align="right" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="status" label="Status" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col="received" label="Received" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <th className="px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((e) => {
                const name = enquiryName(e);
                const primary = primaryAction(e);
                const quote = latestQuotation(e);
                return (
                  <tr
                    key={e.id}
                    onClick={() => onOpen(e.id)}
                    className={`border-b border-stone-100 cursor-pointer transition-colors hover:bg-amber-50/40 ${e.status === "rejected" ? "opacity-70" : ""}`}
                  >
                    <td className="px-4 py-3 min-w-[200px]">
                      <p className="font-semibold text-stone-800">{name}</p>
                      <p className="text-xs text-stone-500">{enquiryPhone(e) || "No phone"}</p>
                    </td>
                    <td className="px-4 py-3 text-stone-700">{e.type || "—"}</td>
                    <td className="px-4 py-3 text-stone-700 whitespace-nowrap">{e.event_date ? formatDate(e.event_date) : "—"}</td>
                    <td className="px-4 py-3 text-right text-stone-700 tabular-nums">{e.guest_count || "—"}</td>
                    <td className="px-4 py-3">
                      <Badge label={statusLabel(e.status)} status={e.status} />
                      {quote && <p className="text-[11px] text-stone-400 mt-0.5 font-mono">{quote.quotation_number || `#${quote.id}`}</p>}
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">{e.created_at ? formatDate(e.created_at) : "—"}</td>
                    <td className="px-4 py-3" onClick={(ev) => ev.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {primary && (
                          <button
                            type="button"
                            onClick={primary.onClick}
                            className="h-8 px-3 rounded-lg text-xs font-semibold whitespace-nowrap text-white bg-amber-500 hover:bg-amber-600 transition-colors"
                          >
                            {primary.label}
                          </button>
                        )}
                        <ActionMenu items={menuItems(e)} label={`More actions for ${name}`} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
